/**
 * SDLC 6.3.1 — FILE: frontend/src/pages/ComplaintsPage.tsx
 * PURPOSE: Complaints list page — navigate target from ComplaintDashboardTiles onClick
 * DATE: 2026-04-09
 * COMPLIANCE: SDLC 6.3.1 + SPEC-0715 Task Complaint & Appeal
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 *
 * @business-context
 * - Domain: Task Complaint & Appeal — Manager view pending/all complaints
 * - Impact: BLOCKER-6 fix — tile click navigate had no destination
 * - Stakeholders: Branch Managers, Tenant Admins
 *
 * @cross-references
 * - API: backend/app/api/v2/complaints.py (GET /pending, /summary)
 * - Tiles: frontend/src/components/ComplaintDashboardTiles.tsx
 * - Dashboard: frontend/src/components/dashboards/ManagerDashboard.tsx:559
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, AlertTriangle, ArrowLeft, FileText } from 'lucide-react';
import { useAuthStore } from '../store/auth';

interface Complaint {
  id: string;
  complaint_type: string;
  reason_text: string;
  status: string;
  shift_date: string;
  created_at: string;
  sla_deadline?: string;
}

interface Summary {
  total: number;
  by_status: Record<string, number>;
  sla_breach_count: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  SUBMITTED: { label: 'Đã gửi', color: 'bg-orange-100 text-orange-800', icon: <Clock className="w-4 h-4" /> },
  UNDER_REVIEW: { label: 'Đang xem xét', color: 'bg-blue-100 text-blue-800', icon: <FileText className="w-4 h-4" /> },
  APPROVED: { label: 'Đã chấp nhận', color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-4 h-4" /> },
  REJECTED: { label: 'Đã từ chối', color: 'bg-red-100 text-red-800', icon: <XCircle className="w-4 h-4" /> },
  ESCALATED: { label: 'Đã leo thang', color: 'bg-purple-100 text-purple-800', icon: <AlertTriangle className="w-4 h-4" /> },
};

const TYPE_LABELS: Record<string, string> = {
  SHIFT_TASK: 'Nhiệm vụ ca',
  CHECKLIST_ITEM: 'Checklist',
  HANDOVER_ITEM: 'Bàn giao',
};

export default function ComplaintsPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.accessToken);
  const tenantKey = useAuthStore((s) => s.tenantKey);

  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      // Token chưa sẵn sàng (zustand chưa rehydrate) — chờ tối đa 3s rồi báo lỗi
      const timeout = setTimeout(() => {
        setLoading(false);
        setError('Phiên đăng nhập hết hạn — vui lòng đăng nhập lại');
      }, 3_000);
      return () => clearTimeout(timeout);
    }

    const apiBase = import.meta.env.VITE_API_BASE_URL || '';
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'X-Tenant-Key': tenantKey || '',
    };

    Promise.all([
      fetch(`${apiBase}/api/v2/complaints/pending`, { headers }).then(r => r.ok ? r.json() : []),
      fetch(`${apiBase}/api/v2/complaints/summary`, { headers }).then(r => r.ok ? r.json() : null),
    ])
      .then(([pendingData, summaryData]) => {
        const list = Array.isArray(pendingData) ? pendingData : pendingData?.items || pendingData?.data || [];
        setComplaints(list);
        setSummary(summaryData);
      })
      .catch(() => setError('Không thể tải danh sách khiếu nại'))
      .finally(() => setLoading(false));
  }, [token, tenantKey]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/dashboard/manager')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Khiếu nại và SLA</h1>
            <p className="text-sm text-gray-500">Danh sách khiếu nại cần xử lý</p>
          </div>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-blue-500">
              <p className="text-sm text-gray-600">Tổng</p>
              <p className="text-2xl font-bold">{summary.total}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-orange-500">
              <p className="text-sm text-gray-600">Chờ xử lý</p>
              <p className="text-2xl font-bold">{(summary.by_status.SUBMITTED || 0) + (summary.by_status.UNDER_REVIEW || 0)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-red-500">
              <p className="text-sm text-gray-600">SLA vi phạm</p>
              <p className="text-2xl font-bold">{summary.sla_breach_count}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-green-500">
              <p className="text-sm text-gray-600">Đã giải quyết</p>
              <p className="text-2xl font-bold">{summary.by_status.APPROVED || 0}</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-500">Đang tải danh sách khiếu nại...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
        )}

        {/* Complaint list */}
        {!loading && !error && (
          <div className="space-y-3">
            {complaints.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">Không có khiếu nại chờ xử lý</p>
                <p className="text-sm text-gray-400 mt-1">Tất cả khiếu nại đã được giải quyết</p>
              </div>
            ) : (
              complaints.map((c) => {
                const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.SUBMITTED;
                return (
                  <div key={c.id} className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-orange-500 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                            {cfg.icon} {cfg.label}
                          </span>
                          <span className="text-xs text-gray-400">
                            {TYPE_LABELS[c.complaint_type] || c.complaint_type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900 mt-1">{c.reason_text}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          Ca ngày {c.shift_date} · Gửi lúc {new Date(c.created_at).toLocaleString('vi-VN')}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
